import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  FormFeedback,
  Form,
  Input,
  Alert,
  Spinner,
} from "reactstrap";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";
import emailjs from "@emailjs/browser";

//Import Section Title
import SectionTitle from "../common/section-title";

//EmailJS: https://dashboard.emailjs.com/admin
//Account: info@inverlux.com
emailjs.init("PA50pjor-d33ayVf1"); // your public key

/**
 * Strip all carriage-return characters from user input.
 *
 * Why not normalize to CRLF ourselves?
 * EmailJS's HTTP transport (and/or the downstream SMTP relay) already
 * performs its own LF → CRLF normalization.  If we send explicit \r\n,
 * the transport doubles it to \r\r\n, producing "bare CR" violations
 * per RFC 822bis §2.3.
 *
 * By stripping every \r we guarantee the payload contains only \n.
 * The transport then converts each \n to \r\n — exactly once —
 * producing fully compliant line endings.
 *
 * This is deterministic and idempotent.
 */
const sanitizeLineEndings = (str) => {
  if (typeof str !== "string") return str;
  return str.replace(/\r/g, ""); // strip all CR; let transport add CRLF
};


/**
 * Sanitize all string values in the template parameters object.
 * Non-string values are passed through unchanged.
 */
const sanitizeTemplateParams = (params) => {
  const sanitized = {};
  for (const [key, value] of Object.entries(params)) {
    sanitized[key] = sanitizeLineEndings(value);
  }
  return sanitized;
};

const ContactUs = () => {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter Your Name"),
      email: Yup.string()
        .email("Invalid email")
        .required("Please Enter Your Email"),
      subject: Yup.string().required("Please Enter Your Subject"),
      // message: Yup.string().required("Please enter a message"),
    }),
    onSubmit: (values, { resetForm }) => {
      setSending(true);

      // Build template parameters from Formik values (not from the DOM).
      // This gives us full control to sanitize line endings before they
      // reach the SMTP server, avoiding the bare-CR / bare-LF violation.
      const templateParams = sanitizeTemplateParams({
        name: values.name,
        email: values.email,
        subject: values.subject,
        message: values.message,
      });

      emailjs
        .send("default_service", "template_2x7r5ls", templateParams)
        .then(() => {
          setSent(true); // show the success banner
          resetForm();
          setSending(false);
        })
        .catch((err) => {
          console.error("EmailJS send failed:", err);
          alert(
            "❌ Your message was NOT sent successfully.\n\n" +
              "Please try again later or contact us directly at info@inverlux.com.\n\n" +
              "Error details: " +
              (err?.text || err?.message || JSON.stringify(err))
          );
          setSending(false);
        });
    },
  });
  return (
    <React.Fragment>
      <section className="section " id="contact">
        <Container>
          {/* Render section title */}
          <SectionTitle
            title="Question?"
            description="Need assistance or have inquiries about our products and services? We're here to help! Reach out to us with any questions, and our team will gladly provide you with the information and support you need."
          />

          <Row>
            <Col lg="4">
              <div className="mt-4 pt-4">
                <p className="mt-4">
                  <span className="h5">Office Address:</span>
                  <br />{" "}
                  <span className="text-muted d-block mt-2">
                    700 El Camino Real Suite 120 #1235,
                    <br />
                    Menlo Park, CA94025
                  </span>
                </p>
                <p className="mt-4">
                  <span className="h5">Support Email:</span>
                  <br />{" "}
                  <span className="text-muted d-block mt-2">
                    info@inverlux.com
                  </span>
                </p>
                <p className="mt-4">
                  <span className="h5">Working Hours:</span>
                  <br />{" "}
                  <span className="text-muted d-block mt-2">
                    9:00AM To 18:00PM PST
                  </span>
                </p>
              </div>
            </Col>
            <Col lg="8">
              <div className="custom-form mt-4 pt-4">
                {sent && (
                  <Alert
                    color="success"
                    toggle={() => window.location.reload()} // clicking "×" reloads page
                  >
                    ✅ Message sent successfully!
                  </Alert>
                )}
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <fieldset disabled={sending}>
                    <Row>
                      <Col lg="6 mt-2">
                        <Input
                          name="name"
                          className=""
                          placeholder="Your name*"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name && validation.errors.name
                              ? true
                              : false
                          }
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.name}
                          </FormFeedback>
                        ) : null}
                      </Col>
                      <Col lg="6 mt-2">
                        <Input
                          name="email"
                          className=""
                          placeholder="Your email*"
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            {validation.errors.email}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12 mt-2">
                        <Input
                          name="subject"
                          className=""
                          placeholder="Your Subject.."
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.subject || ""}
                          invalid={
                            validation.touched.subject &&
                            validation.errors.subject
                              ? true
                              : false
                          }
                        />
                        {validation.touched.subject &&
                        validation.errors.subject ? (
                          <FormFeedback type="invalid">
                            {validation.errors.subject}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12 mt-2">
                        <div className="form-group">
                          <textarea
                            name="message"
                            id="message"
                            rows="4"
                            className="form-control"
                            placeholder="Your message..."
                            value={validation.values.message}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                          ></textarea>
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12" className="text-end">
                        <Button
                          className="submitBnt btn btn-primary"
                          disabled={sending}
                        >
                          {sending ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Sending…
                            </>
                          ) : (
                            "Send Message"
                          )}
                        </Button>
                        <div id="simple-msg"></div>
                      </Col>
                    </Row>
                  </fieldset>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default ContactUs;
